import React, { createContext, useContext, useState, useCallback } from 'react';

export interface SignupMember {
    firstName: string;
    lastName: string;
    email: string;
    role: 'attorney' | 'supportStaff';
    isAdminContact: boolean;
}

export interface SignupSession {
    sessionToken: string;
    email: string;
    firstName: string;
    lastName: string;
    plan: 'free' | 'basic' | 'pro' | null;
    teamName: string;
    members: SignupMember[];
    paymentAmount: number | null;
    paymentMethodType: string | null;
    founderRole: 'attorney' | 'supportStaff';
}

const STORAGE_KEY = 'calculist_signup_session';

const defaultSession: SignupSession = {
    sessionToken: '',
    email: '',
    firstName: '',
    lastName: '',
    plan: null,
    teamName: '',
    members: [],
    paymentAmount: null,
    paymentMethodType: null,
    founderRole: 'attorney',
};

function loadFromStorage(): SignupSession {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...defaultSession };
        return { ...defaultSession, ...JSON.parse(raw) };
    } catch {
        return { ...defaultSession };
    }
}

function saveToStorage(session: SignupSession): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
        // ignore
    }
}

export function clearSignupSession(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

interface SignupSessionContextValue {
    session: SignupSession;
    updateSession: (updates: Partial<SignupSession>) => void;
    clearSession: () => void;
}

const SignupSessionContext = createContext<SignupSessionContextValue | null>(null);

export const SignupSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<SignupSession>(loadFromStorage);

    const updateSession = useCallback((updates: Partial<SignupSession>) => {
        setSession(prev => {
            const next = { ...prev, ...updates };
            saveToStorage(next);
            return next;
        });
    }, []);

    const clearSession = useCallback(() => {
        clearSignupSession();
        setSession({ ...defaultSession });
    }, []);

    return (
        <SignupSessionContext.Provider value={{ session, updateSession, clearSession }}>
            {children}
        </SignupSessionContext.Provider>
    );
};

export function useSignupSession(): SignupSessionContextValue {
    const ctx = useContext(SignupSessionContext);
    if (!ctx) throw new Error('useSignupSession must be used within SignupSessionProvider');
    return ctx;
}
